'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { Button, Heading, Modal, Text } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { analytics } from '@/src/components/features/property/lib';
import type { ContactSubmissionContext } from '@/src/components/features/property/ContactForm/types';

const ContactFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ContactForm/ContactFormOverlay').then((mod) => mod.ContactFormOverlay),
);

const PostContactFlowModal = dynamic(() =>
  import('@/src/components/features/property/ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

export const useApplyAction = (listing?: PropertyDetailListing | null, triggerLocation = 'home_details_halo') => {
  const t = useTranslations('contactForm');
  const [isOpen, setIsOpen] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const acceptsZillowApplications =
    listing?.rentalApplicationStatus?.toLowerCase() === 'accepted' && Boolean(listing.aliasEncoded);

  const openApply = useCallback(() => {
    analytics.emitTrackApplyClickEvent({
      triggerLocation,
      triggerObject: 'home_details_component|modal_apply_form',
      listing,
      phase: 'open',
    });
    setIsOpen(true);
  }, [listing, triggerLocation]);

  const handleSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setIsOpen(false);
    setPostContactContext(context);
  }, []);

  const applyOverlay = acceptsZillowApplications ? (
    <Modal.Root open={isOpen} onOpenChange={setIsOpen} size={{ base: 'fullScreen', lg: 'xs' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content aria-label={t('zillowApplicationTitle')}>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                {t('zillowApplicationTitle')}
              </Heading>
            </Modal.Header>
            <Modal.Body>
              <Text>{t('zillowApplicationBody')}</Text>
            </Modal.Body>
            <Modal.Footer>
              <Button asChild tone="brand" emphasis="filled" fluid>
                <a
                  href={`https://www.zillow.com/renter-hub/applications/listing/${encodeURIComponent(
                    listing.aliasEncoded,
                  )}/rental-application/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('applyOnZillow')}
                </a>
              </Button>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  ) : isOpen || postContactContext ? (
    <>
      {isOpen && (
        <ContactFormOverlay
          open={isOpen}
          onOpenChange={setIsOpen}
          title={t('titleApply')}
          listing={listing}
          mode="requestToApply"
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
      <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
    </>
  ) : null;

  return { openApply, applyOverlay };
};
